import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { authAPI, userAPI } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUserFromToken();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUserFromToken = async () => {
    try {
      const response = await userAPI.getCurrentUser();
      if (response.success) {
        const userData = response.user;
        setUser({
          ...userData,
          name: `${userData.first_name} ${userData.last_name}`,
          emailVerified: userData.email_verified
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden des Benutzers:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (emailOrUsername, password) => {
    try {
      const response = await authAPI.login(emailOrUsername, password);

      if (response.success) {
        localStorage.setItem('token', response.token);
        const userData = response.user;
        setUser({
          ...userData,
          name: `${userData.first_name} ${userData.last_name}`,
          emailVerified: userData.email_verified
        });
        toast.success('Erfolgreich angemeldet!');
        return { success: true };
      } else {
        toast.error('Login fehlgeschlagen');
        return { success: false, error: 'Login fehlgeschlagen' };
      }
    } catch (error) {
      console.error('Login-Fehler:', error);
      const errorMessage = error.response?.data?.error || 'Anmeldefehler';
      const needsVerification = !!error.response?.data?.needsVerification;
      const needsApproval = !!error.response?.data?.needsApproval;
      toast.error(errorMessage);
      return { success: false, error: errorMessage, needsVerification, needsApproval };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register({
        username: userData.username,
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName
      });

      if (response.success) {
        toast.success('Registrierung erfolgreich! Bitte bestätigen Sie Ihre E-Mail.');
        return { success: true };
      } else {
        toast.error('Registrierung fehlgeschlagen');
        return { success: false, error: 'Registrierung fehlgeschlagen' };
      }
    } catch (error) {
      console.error('Registrierungs-Fehler:', error);
      const errorMessage = error.response?.data?.error || 'Registrierungsfehler';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Erfolgreich abgemeldet!');
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await userAPI.updateProfile(profileData);
      if (response.success) {
        // User-Daten neu laden
        await loadUserFromToken();
        toast.success('Profil erfolgreich aktualisiert!');
        return { success: true };
      }
    } catch (error) {
      console.error('Profil-Update-Fehler:', error);
      const errorMessage = error.response?.data?.error || 'Fehler beim Aktualisieren des Profils';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateProfile,
    isAdmin,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
